import { OnInit, Component } from '@angular/core';
import { TenantService, UserService } from '../services';
import { tap, filter } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import * as moment from 'moment';
import { PaymentEditorDialogComponent } from './paymentEditorDialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'user-subscription',
    styles: [`
        .status li {
            margin-top: 5px;
            margin-bottom: 5px;
        } 
    `],
    template: `
        <mat-card>
            <mat-card-title><h2 fxFlex>Subscription</h2></mat-card-title>
            <mat-card-content>
                <div *ngIf="noTouching" fxFlex fxLayoutAlign="center center"><spinner></spinner></div>
                <div *ngIf="!noTouching && isStriped && !isUnpaid && !isExpired" fxFlex fxLayout="column">
                    <p>
                        You are on the {{subscription?.plan?.nickname}} plan. 
                        <span *ngIf="subscription?.status === 'trialing'">You are currently in a free trial which ends on {{toDate(subscription?.current_period_end) | date }}</span>
                        <span *ngIf="subscription?.status !== 'trialing'">Your plan will renew on {{toDate(subscription?.current_period_end) | date }} for {{ (upcomingInvoice?.amount_due / 100) | currency }}.</span>
                    </p>
                    <h3>Account Status</h3>
                    <ul class="status"> 
                        <li *ngIf="defaultSource()">Future charges will be billed to the card ending in {{defaultSource()?.last4}}.</li>
                        <li *ngIf="!defaultSource()">Please add a payment method <a [routerLink]="['../payment']">here</a>.</li>
                        <li>Billing emails are sent to {{customer?.email}}</li>
                    </ul>
                    <h3 *ngIf="subscription?.status !== 'trialing'">Invoices</h3>
                    <table *ngIf="subscription?.status !== 'trialing'" mat-table fxFlex="100" [dataSource]="invoices?.data" style="width: 100%;">
                        <ng-container matColumnDef="date">
                            <th mat-header-cell *matHeaderCellDef>Date</th>
                            <td mat-cell *matCellDef="let row">{{toDate(row.created) | date}}</td>
                        </ng-container>
                        <ng-container matColumnDef="amount">
                            <th mat-header-cell *matHeaderCellDef>Amount</th>
                            <td mat-cell *matCellDef="let row">{{ (row?.amount_due / 100) | currency }}</td>
                        </ng-container>
                        <ng-container matColumnDef="receipt_number">
                            <th mat-header-cell *matHeaderCellDef>Number</th>
                            <td mat-cell *matCellDef="let row">{{ row?.number }}</td>
                        </ng-container>
                        <ng-container matColumnDef="status">
                            <th mat-header-cell *matHeaderCellDef>Status</th>
                            <td mat-cell *matCellDef="let row">{{ row?.status }}</td>
                        </ng-container>
                        <ng-container matColumnDef="invoice">
                            <th mat-header-cell *matHeaderCellDef>Download</th>
                            <td mat-cell *matCellDef="let row">
                                <a *ngIf="row.status && row.status !== 'draft'" [href]="row.invoice_pdf"><mat-icon fontSet="fontawesome" fontIcon="fa-file-pdf" class="action-button"></mat-icon></a>
                                <span *ngIf="row.status && row.status=== 'draft'">Unpaid</span>
                            </td>
                        </ng-container>

                        <tr mat-header-row *matHeaderRowDef="['receipt_number', 'date', 'amount', 'status', 'invoice']"></tr>
                        <tr mat-row *matRowDef="let row; columns: ['receipt_number', 'date', 'amount', 'status', 'invoice'];"></tr>
                    </table>
                </div>
                <div *ngIf="!noTouching && !isStriped && !isExpired && !isUnpaid" fxFlex fxLayout="column">
                    <p style="max-width: 550px;">
                        You are currently in a free trial<span *ngIf="user?.trialEndDate"> which ends on {{user.trialEndDate | date }}</span>.
                        <span *ngIf="plan">After that your plan is {{ (plan?.amount / 100) | currency:'USD' }} per month.</span> 
                        Enter a payment method below to continue your subscription after your trial. 
                        You will not be billed until your trial is over and you can cancel at anytime.
                    </p>
                </div>
                <div *ngIf="!noTouching && isExpired" fxFlex fxLayout="column">
                    <p>Your trial has expired.</p>
                </div>
            </mat-card-content>
        </mat-card>
        <mat-card style="margin-top: 20px;">
            <mat-card-title><h2 fxFlex>Payment Methods</h2></mat-card-title>
            <mat-card-content>
                <div fxLayout="column">
                    <div *ngIf="isExpired || isUnpaid" fxFlex>
                        <h3 *ngIf="isExpired">The trial period for your access to {{tenant?.hostName}} has expired.</h3>
                        <h3 *ngIf="isUnpaid">Your access to {{tenant?.hostName}} has been disabled due to failed payment attempts.</h3>
                        <h5>We hope that you will continue with {{tenant?.name}} and we are standing ready to help in any way we can.</h5>
                        <h5>To restore your workspace please select Add Payment Method below and complete the credit card information to continue. </h5>
                        <h5>If you have any question please contact us at support@attensa.com.</h5>
                    </div>
                    <div *ngIf="noTouching" fxFlex fxLayoutAlign="center center"><spinner></spinner></div>
                    <table mat-table fxFlex *ngIf="!noTouching" [dataSource]="paymentMethods">
                        <ng-container matColumnDef="details">
                            <th mat-header-cell *matHeaderCellDef></th>
                            <td mat-cell *matCellDef="let row">
                                <mat-icon aria-label="Card Type"
                                        style="font-size: 20px; margin-right: 5px;"
                                        fontSet="fontawesome"
                                        [ngClass]="{ 'fa-cc-visa': row.brand === 'Visa',
                                                    'fa-cc-mastercard': row.brand === 'MasterCard',
                                                    'fa-cc-amex': row.brand === 'American Express',
                                                    'fa-cc-discover': row.brand === 'Discover' }">
                                </mat-icon>
                                <span *ngIf="row.funding === 'debit'"> Debit</span>
                                <span *ngIf="row.funding === 'prepaid'"> Prepaid</span>
                                <span> {{row.brand}}</span> ending in {{row.last4}}
                            </td>
                        </ng-container>
                        <ng-container matColumnDef="exp">
                            <th mat-header-cell *matHeaderCellDef></th>
                            <td mat-cell *matCellDef="let row">expires {{row.exp_month}}/{{row.exp_year}}</td>
                        </ng-container>
                        <ng-container matColumnDef="actions">
                            <th mat-header-cell *matHeaderCellDef></th>
                            <td mat-cell *matCellDef="let row">
                                <button mat-icon-button 
                                        *ngIf="!isDefault(row)" 
                                        (click)="makePaymentMethodDefault(row)"
                                        matTooltip="Make Default">
                                    <mat-icon fontSet="fontawesome" fontIcon="fa-circle"></mat-icon>
                                </button>
                                <mat-icon *ngIf="isDefault(row)"
                                        aria-label="Default"
                                        fontSet="fontawesome"
                                        fontIcon="fa-dot-circle"
                                        style="padding-left: 15px;"
                                        matTooltip="Default"></mat-icon>
                                <button mat-icon-button  *ngIf="!isDefault(row)"  (click)="removePaymentMethod(row)" matTooltip="Remove">
                                    <mat-icon fontSet="fontawesome" fontIcon="fa-remove" class="action-button"></mat-icon>
                                </button>
                            </td>
                        </ng-container>
                        <tr mat-header-row *matHeaderRowDef="['details', 'exp', 'actions']"></tr>
                        <tr mat-row *matRowDef="let row; columns: ['details', 'exp', 'actions'];"></tr>
                    </table>
                </div>
            </mat-card-content>
            <mat-card-actions fxLayout="row" fxLayoutAlign="end center">
                <button mat-raised-button (click)="addPaymentMethod()">Add Payment Method</button>
            </mat-card-actions>
        </mat-card>
    `
})
export class UserSubscriptionComponent implements OnInit {
    customer: any;
    noTouching = true;
    subscription: any;
    upcomingInvoice: any;
    invoices: any;
    isStriped: boolean;
    trialEndDate: any;
    stripePricingPlanId: any;
    isExpired: boolean;
    isUnpaid: boolean;
    paymentMethods: any[] = [];
    tenant: any;
    plan: any;
    user: any;

    constructor(private dialog: MatDialog, 
                private tenantService: TenantService, 
                private userService: UserService) {}

    ngOnInit(): void {
        this.refreshUser();
        
        this.tenantService.getCurrentTenant()
        .pipe(
            tap(tenant => this.stripePricingPlanId = tenant.settings.stripePricingPlanId),
            tap(tenant => this.tenant = tenant)
        ).subscribe(() => this.noTouching = false);

        this.tenantService.fetchPlan().subscribe(plan => this.plan = plan);
    }

    defaultSource() {
        return this.customer ? this.customer.sources.data.find(source => source.id === this.customer.default_source) : undefined;
    }

    toDate(timestamp) {
        return timestamp * 1000;
    }

    isDefault(paymentMethod): boolean {
        return paymentMethod.id === this.customer.default_source;
    } 

    fetchCustomer(): void {
        forkJoin([
            this.userService.fetchSubscriptionCustomer(),
            this.userService.fetchSubscriptionUpcomingInvoice(),
            this.userService.fetchSubscriptionInvoices()
        ])
        .subscribe(([customer, upcomingInvoice, invoices]) => {
            this.customer = customer;
            this.paymentMethods = this.customer && this.customer.sources ? this.customer.sources.data : [];
            this.noTouching = false;
            if (customer && customer.subscriptions && customer.subscriptions.data[0]) {
                this.subscription = customer.subscriptions.data[0];
            }
            this.upcomingInvoice = upcomingInvoice;
            this.invoices = invoices;
        });
    }

    makePaymentMethodDefault(card): void {
        this.noTouching = true;
        this.userService.makeSubscriptionCardDefault(card).subscribe(() => this.fetchCustomer());
    }

    removePaymentMethod(card):void {
        this.noTouching = true;
        this.userService.removeSubscriptionCard(card.id).subscribe(() => this.fetchCustomer());
    }

    addPaymentMethod(): void {
        this.dialog.open(PaymentEditorDialogComponent, {data: { isUser: true }}).afterClosed()
        .pipe(filter(event => !!event))
        .subscribe(() => this.refreshUser());
    }

    refreshUser() {
        this.userService.fetchCurrentUser().pipe(
            tap(user => this.isExpired = (user.status === 'TRIAL_EXPIRED')),
            tap(user => this.isUnpaid = (user.status === 'UNPAID')),
            tap(user => this.trialEndDate = user.trialEndDate ? moment(user.trialEndDate).toDate() : undefined),
            tap(user => this.isStriped = !!user.stripeCustomerId),
            tap(user => this.user = user),
            filter(user => !!user.stripeCustomerId)
        )
        .subscribe(() => this.fetchCustomer());
    }
}